import { MIN_TRACK_WIDTH_PX, type Breakpoint } from './constants';
import type { MatrixFieldInput, MatrixTolerance } from './types';

export interface PackedFieldSlot {
  id: string;
  actualCols: number;
  maxCols: number;
  minWidthPx?: number;
  maxWidthPx?: number;
}

export interface InferSuperstructureInput {
  idealCols: number;
  containerWidth: number;
  columnGapPx: number;
  fields: MatrixFieldInput[];
  breakpoint: Breakpoint;
  columnsAtBp: Record<Breakpoint, number>;
}

/** Resolve vision tolerance at a breakpoint (clamped to column count). */
export function resolveToleranceAtBreakpoint(
  field: MatrixFieldInput,
  bp: Breakpoint,
  columnsAtBp: Record<Breakpoint, number>,
): MatrixTolerance {
  const colCount = columnsAtBp[bp];
  return {
    minCols: Math.max(1, Math.min(field.tolerance.minCols, colCount)),
    maxCols: Math.max(1, Math.min(field.tolerance.maxCols, colCount)),
    minWidthPx: field.tolerance.minWidthPx,
    maxWidthPx: field.tolerance.maxWidthPx,
  };
}

/**
 * Phase 1 — infer superstructure N and gear-shift down until pixel floors fit.
 * Ported from quantized_matrix_ui_engine-v6.html calculateLayout Phase 1.
 */
export function inferSuperstructure(input: InferSuperstructureInput): number {
  const { idealCols, containerWidth, columnGapPx, fields, breakpoint, columnsAtBp } = input;

  if (containerWidth <= 0 || idealCols <= 1) return Math.max(1, idealCols);

  const hasPixelFloors = fields.some((field) => {
    const tol = resolveToleranceAtBreakpoint(field, breakpoint, columnsAtBp);
    return tol.minWidthPx != null && tol.minWidthPx > 0;
  });

  let superstructureCols = Math.max(1, idealCols);

  while (superstructureCols > 1) {
    const totalGaps = (superstructureCols - 1) * columnGapPx;
    const colWidth = (containerWidth - totalGaps) / superstructureCols;

    if (colWidth < MIN_TRACK_WIDTH_PX) {
      superstructureCols--;
      continue;
    }

    if (!hasPixelFloors) break;

    let canFit = true;

    for (const field of fields) {
      const tol = resolveToleranceAtBreakpoint(field, breakpoint, columnsAtBp);
      if (tol.minWidthPx == null || tol.minWidthPx <= 0) continue;

      const minC = Math.min(tol.minCols, superstructureCols);
      const allocatedPx = minC * colWidth + Math.max(0, minC - 1) * columnGapPx;

      if (allocatedPx < tol.minWidthPx) {
        canFit = false;
        break;
      }
    }

    if (canFit) break;
    superstructureCols--;
  }

  return Math.max(1, superstructureCols);
}

/**
 * Phase 2 — distribute leftover structural columns across expandable fields.
 * Exact port of quantized_matrix_ui_engine-v6-debug-2.html distributeLeftoverSpace.
 * Includes Gridline Resonance guard: refuses to expand if next column would exceed maxWidthPx.
 */
export function distributeLeftoverSpace(
  row: PackedFieldSlot[],
  leftover: number,
  colWidth?: number,
  columnGapPx?: number,
): PackedFieldSlot[] {
  if (leftover <= 0 || row.length === 0) return row;

  const slots = row.map((slot) => ({ ...slot }));
  let remaining = leftover;

  const getExpandableFields = () => slots.filter((slot) => {
    if (slot.actualCols >= slot.maxCols) return false;

    if (colWidth != null && columnGapPx != null) {
      const fMaxPx = slot.maxWidthPx ?? Infinity;
      const nextCols = slot.actualCols + 1;
      const predictedAllocatedPx = (nextCols * colWidth) + ((nextCols - 1) * columnGapPx);

      if (predictedAllocatedPx > fMaxPx) {
        return false;
      }
    }

    return true;
  });

  let expandableFields = getExpandableFields();

  while (remaining > 0 && expandableFields.length > 0) {
    for (let i = 0; i < expandableFields.length && remaining > 0; i++) {
      expandableFields[i].actualCols++;
      remaining--;
    }
    expandableFields = getExpandableFields();
  }

  return slots;
}

/** Sum of minCols for a single logical row group (vision Directive 1 baseline). */
export function inferIdealColsFromFields(
  fields: MatrixFieldInput[],
  breakpoint: Breakpoint,
  columnsAtBp: Record<Breakpoint, number>,
): number {
  let sum = 0;
  for (const field of fields) {
    const tol = resolveToleranceAtBreakpoint(field, breakpoint, columnsAtBp);
    sum += tol.minCols;
  }
  return Math.max(1, sum);
}

/**
 * Column width used when packing a field into the current row.
 * Fields with minWidthPx prefer maxCols when they fit — prevents the next row's
 * fields from bleeding in (e.g. phone/email row vs residency).
 * When the next field would fit after our min width, we either fill remaining
 * columns (pushing the next field to the next row) or cap width so the row-mate
 * keeps its minimum span.
 */
export function resolvePackCols(
  minCols: number,
  maxCols: number,
  currentUsedCols: number,
  colCount: number,
  minWidthPx?: number,
  nextFieldMinCols = 0,
): number {
  if (maxCols <= minCols) return minCols;

  const preferMaxAtPack = minWidthPx != null && minWidthPx > 0;
  if (!preferMaxAtPack) return minCols;

  const remainingSlots = colCount - currentUsedCols;
  const preferred = Math.min(maxCols, colCount);
  const fillRow = Math.min(maxCols, remainingSlots);
  const nextFitsAfterFill =
    nextFieldMinCols > 0 && currentUsedCols + fillRow + nextFieldMinCols <= colCount;
  const nextFitsAfterMin =
    nextFieldMinCols > 0 && currentUsedCols + minCols + nextFieldMinCols <= colCount;

  // Row tail (e.g. email): next field would fit if we only take minCols — fill the row first
  if (nextFitsAfterMin && fillRow >= minCols && !nextFitsAfterFill && fillRow >= remainingSlots) {
    return fillRow;
  }

  let capped = preferred;
  while (capped > minCols && currentUsedCols + capped + nextFieldMinCols > colCount) {
    capped--;
  }

  if (currentUsedCols + capped <= colCount) return capped;

  if (currentUsedCols + minCols <= colCount) return minCols;

  return capped;
}
